// Discover.jsx
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAnime } from "../context/AnimeContext";
import { useHorizontalDragScroll } from "../components/useHorizontalDragScroll";

import AnimeCard from "../components/AnimeCard";
import GenreFilter from "../components/GenreFilter";
import DiscoverSpotlight from "../components/DiscoverSpotlight";
import UpcomingAnimeBar from "../components/UpcomingAnimeBar";

// Single source of truth trailers component
import MostPopularTrailers from "../components/MostPopularTrailers";

import { fetchTrailers } from "../services/trailers";

import {
  Flame,
  TrendingUp,
  Radio,
  Disc,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

function Discover() {
  const navigate = useNavigate();
  const { animeList } = useAnime();
  const safeList = Array.isArray(animeList) ? animeList : [];

  // Fetch trailers from API
  const [trailers, setTrailers] = useState([]);
  useEffect(() => {
    fetchTrailers().then(setTrailers).catch(console.error);
  }, []);

  const featuredPicks = useMemo(() => {
    const shuffled = safeList.slice().sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 16);
  }, [safeList]);

  const topRatedPicks = useMemo(() => {
    const sorted = safeList
      .slice()
      .sort((a, b) => (b.AverageRating || 0) - (a.AverageRating || 0));
    return sorted.slice(0, 28);
  }, [safeList]);

  const currentlyAiring = useMemo(() => {
    const airing = safeList.filter((a) => {
      const s = String(a?.Status || a?.status || "").toUpperCase();
      return s === "RELEASING" || s === "AIRING" || s === "ONGOING";
    });

    const MIN_VISIBLE = 12;
    const TARGET = 22;

    if (airing.length >= MIN_VISIBLE) return airing.slice(0, TARGET);

    const existing = new Set(airing.map((x) => x?.AnimeID));
    const fillers = safeList
      .filter((x) => !existing.has(x?.AnimeID))
      .sort(() => Math.random() - 0.5);

    return [...airing, ...fillers].slice(0, TARGET);
  }, [safeList]);

  const goToSearch = () => navigate("/search");

  const CardFrame = ({ children, heightClass, widthClass }) => (
    <div className={`${widthClass} flex-shrink-0 snap-start`}>
      <div
        className={`relative overflow-hidden rounded-2xl ${heightClass} pb-[2px]`}
      >
        <div className="w-full min-h-full">{children}</div>
      </div>
    </div>
  );

  const RowShell = ({ title, icon, accentClass, children, rightSlot }) => {
    const { ref, bind } = useHorizontalDragScroll({ dragThresholdPx: 7 });

    const scrollByAmount = (dir) => {
      const el = ref.current;
      if (!el) return;
      const amount = Math.round(el.clientWidth * 0.9) * dir;
      el.scrollBy({ left: amount, behavior: "smooth" });
    };

    // Match trailers: floating chevrons only (no circle)
    // - generous invisible hit area
    // - subtle depth
    const arrowBtn =
      "pointer-events-auto inline-flex items-center justify-center " +
      "h-12 w-12 2xl:h-14 2xl:w-14 " +
      "bg-transparent border-0 rounded-none p-0 " +
      "text-white/70 hover:text-white " +
      "drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)] " +
      "transition duration-200";

    const desktopRailPad = "xl:px-14 2xl:px-16";
    const DESKTOP_XL = 1280;
    const isDesktopXL = () =>
      typeof window !== "undefined" && window.innerWidth >= DESKTOP_XL;

    return (
      <section className="mt-6 sm:mt-8 xl:mt-10">
        {/* Header stays aligned with the page */}
        <div
          className={`flex items-end justify-between gap-2 sm:gap-3 ${desktopRailPad}`}
        >
          <h2
            className={[
              "flex items-center gap-2 font-bold",
              "text-[18px] leading-tight sm:text-xl xl:text-2xl",
              accentClass,
            ].join(" ")}
          >
            {icon} {title}
          </h2>

          {rightSlot ? <div className="flex-shrink-0">{rightSlot}</div> : null}
        </div>

        {/* Row container */}
        <div className={["mt-3 sm:mt-4", desktopRailPad].join(" ")}>
          <div className="relative overflow-visible">
            {/* Desktop arrows OUTSIDE the rail (match trailers) */}
            <div className="hidden xl:flex absolute inset-y-0 -left-12 2xl:-left-16 items-center z-20 pointer-events-none">
              <button
                type="button"
                onClick={() => scrollByAmount(-1)}
                className={arrowBtn}
                aria-label="Scroll left"
                title="Scroll left"
              >
                <ChevronLeft className="h-6 w-6 2xl:h-8 2xl:w-8 -translate-x-[2px]" />
              </button>
            </div>

            <div className="hidden xl:flex absolute inset-y-0 -right-12 2xl:-right-16 items-center z-20 pointer-events-none">
              <button
                type="button"
                onClick={() => scrollByAmount(1)}
                className={arrowBtn}
                aria-label="Scroll right"
                title="Scroll right"
              >
                <ChevronRight className="h-6 w-6 2xl:h-8 2xl:w-8 translate-x-[2px]" />
              </button>
            </div>

            {/* Scroll rail */}
            <div
              ref={ref}
              className={[
                "hide-scroll flex overflow-x-auto scroll-smooth min-h-0",
                "gap-3 sm:gap-4",
                "px-3 sm:px-4",
                "py-3 sm:py-4",
                "snap-x snap-mandatory",
                "[scrollbar-width:none] [-ms-overflow-style:none]",
                "touch-pan-x",
                "cursor-grab select-none",
                "w-full",
                "xl:cursor-auto",
              ].join(" ")}
              onClickCapture={(e) => {
                if (isDesktopXL()) return;
                bind.onClickCapture?.(e);
              }}
              onPointerDown={(e) => {
                if (isDesktopXL()) return;
                bind.onPointerDown?.(e);
              }}
              onPointerMove={(e) => {
                if (isDesktopXL()) return;
                bind.onPointerMove?.(e);
              }}
              onPointerUp={(e) => {
                if (isDesktopXL()) return;
                bind.onPointerUp?.(e);
              }}
              onPointerCancel={(e) => {
                if (isDesktopXL()) return;
                bind.onPointerCancel?.(e);
              }}
              onPointerLeave={(e) => {
                if (isDesktopXL()) return;
                bind.onPointerLeave?.(e);
              }}
              onWheel={(e) => {
                bind.onWheel?.(e);
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const SearchBarCTA = ({ className = "" }) => (
    <button
      type="button"
      onClick={goToSearch}
      className={[
        "group inline-flex items-center",
        "h-10 sm:h-11",
        "rounded-2xl",
        "border border-white/12",
        "bg-white/[0.04]",
        "hover:bg-white/[0.06] hover:border-white/18",
        "transition",
        "focus:outline-none focus:ring-2 focus:ring-emerald-300/20",
        className,
      ].join(" ")}
      aria-label="Go to search"
      title="Search"
    >
      <div className="flex items-center gap-2 px-3.5 sm:px-4">
        <Search
          size={16}
          className="text-white/55 group-hover:text-white/75 transition"
        />
        <span className="text-[12px] sm:text-[13px] font-semibold text-white/70 group-hover:text-white/90 transition">
          Search
        </span>
      </div>

      <div className="ml-2 h-5 w-px bg-white/10" />
      <div className="flex-1" />

      <span className="pr-3.5 sm:pr-4 text-[11px] font-semibold tracking-wide text-white/35 group-hover:text-white/45 transition">
        Explore
      </span>
    </button>
  );

  // Trailer header that matches your rail padding AND keeps the arrows unclipped
  const TrailersHeaderRow = () => {
    const desktopRailPad = "xl:px-14 2xl:px-16";
    return (
      <div
        className={[
          "flex items-end justify-between gap-3 px-1",
          desktopRailPad,
        ].join(" ")}
      >
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.35em] uppercase text-white/45">
            Featured
          </p>
          <p className="mt-1 text-sm font-semibold text-white/85 truncate">
            Most Popular Trailers
          </p>
        </div>
      </div>
    );
  };



  return (
    <div
      className={`min-h-screen text-white overflow-x-hidden`}
    >
      <div className="pointer-events-none fixed inset-0">
        <div
          className="
            absolute inset-0 opacity-[0.45]
            [background-image:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)]
            [background-size:160px_160px]
            [background-position:12px_24px]
          "
        />
        <div
          className="
            absolute inset-0 opacity-[0.35]
            [background-image:radial-gradient(rgba(255,255,255,0.4)_1px,transparent_1px)]
            [background-size:110px_110px]
            [background-position:73px_41px]
          "
        />
        <div
          className="
            absolute inset-0 opacity-[0.25]
            [background-image:radial-gradient(rgba(255,255,255,0.9)_1px,transparent_1px)]
            [background-size:320px_320px]
            [background-position:180px_120px]
          "
        />
      </div>

      <style>{`
        .hide-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      <div className="relative mx-auto w-full max-w-[1500px] px-3 sm:px-4 md:px-6 pt-4 sm:pt-6 pb-12 sm:pb-14">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6 xl:gap-8">
          <main className="min-w-0">
            {/* Discover title above search bar (mobile + desktop) */}
            <div className="mb-4 sm:mb-5">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-white/50">
                    Discover
                  </p>
                </div>

                {/* Search bar stretches full width of main column on desktop */}
                <div className="w-full">
                  <SearchBarCTA className="w-full" />
                </div>
              </div>
            </div>

            {!safeList.length ? (
              <div className="rounded-2xl border border-white/10 bg-white/6 backdrop-blur-xl p-5 sm:p-6 text-white/70">
                Loading anime…
              </div>
            ) : (
              <>
                <DiscoverSpotlight
                  list={safeList}
                  label="Trending"
                  heightClass="h-[320px] sm:h-[400px] md:h-[460px]"
                  intervalMs={8500}
                  maxItems={10}
                />

                {/* Trailers */}
                <section className="mt-6 sm:mt-7">
                  <TrailersHeaderRow />
                  <div className="relative mt-3 xl:px-14 2xl:px-16 overflow-visible">
                    <MostPopularTrailers
                      items={trailers}
                      variant="discover"
                      className="overflow-visible"
                    />
                  </div>
                </section>

                <RowShell
                  title="Featured Picks"
                  icon={<Flame size={18} />}
                  accentClass="text-emerald-300"
                >
                  {featuredPicks.map((anime) => (
                    <CardFrame
                      key={anime.AnimeID}
                      heightClass="min-h-[190px] sm:min-h-[218px] md:min-h-[238px]"
                      widthClass="w-[240px] xs:w-[260px] sm:w-[340px] md:w-[410px]"
                    >
                      <AnimeCard anime={anime} variant="trendingOverlay" />
                    </CardFrame>
                  ))}
                </RowShell>

                <RowShell
                  title="Top Rated"
                  icon={<TrendingUp size={18} />}
                  accentClass="text-yellow-300"
                >
                  {topRatedPicks.map((anime) => (
                    <CardFrame
                      key={anime.AnimeID}
                      heightClass="min-h-[170px] sm:min-h-[198px] md:min-h-[216px]"
                      widthClass="w-[220px] xs:w-[240px] sm:w-[300px] md:w-[350px]"
                    >
                      <AnimeCard anime={anime} variant="trendingOverlay" />
                    </CardFrame>
                  ))}
                </RowShell>

                <RowShell
                  title="Currently Airing"
                  icon={<Radio size={18} />}
                  accentClass="text-emerald-300"
                  rightSlot={
                    <span className="text-[11px] sm:text-xs px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/12 text-emerald-100">
                      {currentlyAiring.length} shows
                    </span>
                  }
                >
                  {currentlyAiring.map((anime) => (
                    <CardFrame
                      key={anime.AnimeID}
                      heightClass="min-h-[190px] sm:min-h-[218px] md:min-h-[238px]"
                      widthClass="w-[240px] xs:w-[260px] sm:w-[340px] md:w-[410px]"
                    >
                      <AnimeCard anime={anime} variant="trendingOverlay" />
                    </CardFrame>
                  ))}
                </RowShell>

                <section className="mt-8 sm:mt-10">
                  <div className="rounded-2xl sm:rounded-[28px] border border-white/12 bg-white/7 backdrop-blur-xl shadow-[0_30px_120px_rgba(0,0,0,0.55)] overflow-hidden">
                    <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b border-white/10 flex items-center gap-2">
                      <Disc
                        size={14}
                        className="text-emerald-200 sm:w-4 sm:h-4"
                      />
                      <p className="text-xs sm:text-sm font-semibold text-white">
                        Browse by Genre
                      </p>
                      <span className="ml-auto text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-white/45">
                        Filters
                      </span>
                    </div>
                    <div className="p-3 sm:p-4 md:p-6">
                      <GenreFilter />
                    </div>
                  </div>
                </section>
              </>
            )}

            <section className="xl:hidden mt-10 space-y-4">
              <div className="pt-2">
                <UpcomingAnimeBar items={safeList} title="Upcoming" maxItems={12} />
              </div>
            </section>
          </main>

          <aside className="hidden xl:block">
            <div className="sticky top-24 space-y-4">
              <div
                className={[
                  "relative overflow-hidden rounded-3xl",
                  "bg-[#050812]",
                  "shadow-[0_26px_95px_rgba(0,0,0,0.68)]",
                  "p-4",
                ].join(" ")}
              >
                <div className="pointer-events-none absolute inset-0">
                  <div className="absolute inset-2 rounded-[22px] border border-white/8" />
                  <div className="absolute inset-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" />
                </div>

                <div className="relative">
                  <UpcomingAnimeBar items={safeList} title="Upcoming" maxItems={12} />
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Discover;
